// lib/utils/licenses.ts
import type { License } from 'lib/types/license'

/**
 * True if the user currently has an assigned standalone voice license in this org.
 */
export function hasVoiceLicenseForOrg(args: {
  userId?: string | null
  orgId?: string | null
  licenses?: License[] | null
}): boolean {
  const { userId, orgId, licenses } = args
  if (!userId || !orgId || !licenses || licenses.length === 0) return false

  return licenses.some(l => l.orgId === orgId && l.userId === userId && l.type === 'standalone_voice')
}

/**
 * Optional: return the actual License record (useful for deletes).
 */
export function getVoiceLicenseForOrg(args: {
  userId?: string | null
  orgId?: string | null
  licenses?: License[] | null
}): License | undefined {
  const { userId, orgId, licenses } = args
  if (!userId || !orgId || !licenses || licenses.length === 0) return undefined
  return licenses.find(l => l.orgId === orgId && l.userId === userId && l.type === 'standalone_voice')
}

/**
 * True if the user may use voice features (listen, PTT). Anyone can view
 * channels, but only licensed users can use them. A super admin needs a
 * license in this exact org; other users need any license in the loaded
 * list, matching the header's PTT gating.
 */
export function canUseVoice(args: {
  userId?: string | null
  orgId?: string | null
  licenses?: License[] | null
  superAdmin: boolean
}): boolean {
  const { userId, licenses, superAdmin } = args
  if (superAdmin) return hasVoiceLicenseForOrg(args)
  return Boolean(userId && licenses?.some(l => l.userId === userId))
}
